import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './IndonesiaBankAccount.css';
import axios from 'axios';
import { initializeTracker } from '../tracker_script.js';
import SEO from './SEO';

const IndonesiaBankAccount = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    nationality: '',
    contact: '',
    arrivalDate: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    error: null
  });

    useEffect(() => {
      // Add meta tags to head
      const usernameMeta = document.createElement('meta');
      usernameMeta.name = 'username';
      usernameMeta.content = 'user_2o6eptKfyuvEOe6kSTFVnGzxmMU';
      document.head.appendChild(usernameMeta);
  
      const offerMeta = document.createElement('meta');
      offerMeta.name = 'offer';
      offerMeta.content = 'Indonesia Bank Account';
      document.head.appendChild(offerMeta);
  
      // Initialize the tracker
      try {
        initializeTracker();
        console.log('Tracker initialized successfully');
      } catch (error) {
        console.error('Error initializing tracker:', error);
      }
  
      // Cleanup function
      return () => {
        if (document.querySelector('meta[name="username"]')) {
          document.querySelector('meta[name="username"]').remove();
        }
        if (document.querySelector('meta[name="offer"]')) {
          document.querySelector('meta[name="offer"]').remove();
        }
      };
    }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, error: null });

    try {
      await axios.post('https://expater-server.vercel.app/api/indonesia-bank-contact', {
        ...formData,
        subject: 'Indonesian Bank Account Inquiry'
      });
      navigate('/indonesia-bank-thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus({
        submitting: false,
        error: 'There was an error submitting your inquiry. Please try again later.'
      });
    }
  };

  return (
    <div className="indonesia-bank-container">
      <SEO
        title="Open Indonesian Bank Account Without KITAS | Bali | Internet Made Media FZCO"
        description="Open a personal bank account in Bali, Indonesia without a KITAS residence permit. Tourist visa accepted. Minimum 500 million IDR deposit. Professional assistance for $250."
        keywords="Indonesian bank account, open bank account Bali, bank account without KITAS, Indonesia bank account tourist, Bali expat banking, Indonesia non-resident bank account"
        canonical="https://tuomaskivioja.com/indonesia-bank-account"
        ogImage="https://tuomaskivioja.com/logo512.png"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Indonesian Bank Account Opening Without KITAS",
          "provider": {
            "@type": "Organization",
            "name": "Internet Made Media FZCO"
          },
          "description": "Professional assistance for opening a personal bank account in Bali, Indonesia without a residence permit",
          "areaServed": "ID",
          "offers": {
            "@type": "Offer",
            "price": "250",
            "priceCurrency": "USD",
            "description": "Assisted bank account opening in Bali without KITAS"
          },
          "url": "https://tuomaskivioja.com/indonesia-bank-account"
        }}
      />

      <div className="indonesia-bank-header">
        <h1>Open an Indonesian Bank Account as a Non-Resident</h1>
        <div className="indonesia-flag">🇮🇩</div>
      </div>

      <div className="indonesia-bank-intro">
        <p>
          Most people assume you need a residence permit to open a local bank account — and that is normally true.
          However, with the right contact and a qualifying deposit, you can open a personal bank account in Bali on a tourist visa.
          We handle the entire process so your appointment goes smoothly.
        </p>
      </div>

      <div className="indonesia-bank-content">
        <section className="indonesia-bank-info">
          <h2>Requirements</h2>

          <div className="requirements-grid">
            <div className="requirement-card">
              <div className="requirement-icon">💰</div>
              <div className="requirement-body">
                <h3>Minimum Deposit</h3>
                <p>You must deposit a minimum of <strong>500 million IDR</strong> (approx. $30,000 USD) to open the account. This balance must be maintained with the bank.</p>
              </div>
            </div>

            <div className="requirement-card">
              <div className="requirement-icon">🛂</div>
              <div className="requirement-body">
                <h3>Valid Indonesian Visa</h3>
                <p>A tourist visa (for example, C1 visa) is sufficient. You do <strong>not</strong> need a KITAS or any residence permit.</p>
              </div>
            </div>

            <div className="requirement-card">
              <div className="requirement-icon">📍</div>
              <div className="requirement-body">
                <h3>Physical Presence Required</h3>
                <p>You <strong>must be in Bali</strong> for the bank appointment. This cannot be done remotely or by proxy.</p>
              </div>
            </div>

            <div className="requirement-card">
              <div className="requirement-icon">🪪</div>
              <div className="requirement-body">
                <h3>Original Passport</h3>
                <p>Your <strong>original passport</strong> must be presented in person at the time of the appointment. Photocopies are not accepted.</p>
              </div>
            </div>
          </div>

          <div className="what-you-get-box">
            <h2>What You Get</h2>
            <ul className="what-you-get-list">
              <li>
                <span className="wug-icon">🏦</span>
                <div>
                  <strong>Premier Banking Account</strong> with a dedicated account manager available to assist you personally
                </div>
              </li>
              <li>
                <span className="wug-icon">🌍</span>
                <div>
                  <strong>Multi-Currency Account</strong> with the ability to hold and spend in multiple currencies
                </div>
              </li>
              <li>
                <span className="wug-icon">📈</span>
                <div>
                  <strong>Earn Interest</strong> on balances held in multiple currencies
                </div>
              </li>
              <li>
                <span className="wug-icon">💳</span>
                <div>
                  <strong>Debit Card</strong> with local currency cash withdrawals available across multiple countries
                </div>
              </li>
            </ul>
          </div>

          <div className="process-box">
            <h2>How It Works</h2>
            <ol className="process-steps">
              <li>
                <span className="step-number">1</span>
                <div className="step-content">
                  <strong>Submit your inquiry</strong> using the form on this page.
                </div>
              </li>
              <li>
                <span className="step-number">2</span>
                <div className="step-content">
                  <strong>We confirm eligibility</strong> and schedule your bank appointment around your time in Bali.
                </div>
              </li>
              <li>
                <span className="step-number">3</span>
                <div className="step-content">
                  <strong>Attend the appointment</strong> in person with your passport and initial deposit ready.
                </div>
              </li>
              <li>
                <span className="step-number">4</span>
                <div className="step-content">
                  <strong>Account opened</strong> — you walk out with a functioning Indonesian bank account.
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section className="contact-form-section">
          <div className="pricing-box">
            <h3>💵 Service Fee — $250</h3>
            <ul className="pricing-benefits">
              <li>Introduction to the banker who will open your account</li>
              <li>Bank appointment coordination</li>
              <li>Document checklist &amp; preparation guidance</li>
            </ul>
            <p className="pricing-note">
              <em>The 500 million IDR minimum deposit is paid directly to the bank — it is not part of our fee.</em>
            </p>
          </div>

          <h2>Inquire About Bank Account Opening</h2>
          <p>Fill out the form below and we'll get back to you within 24 hours.</p>

          <form onSubmit={handleSubmit} className="indonesia-bank-form">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="nationality">Nationality / Passport Country *</label>
              <input
                type="text"
                id="nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                placeholder="e.g. Australian"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="arrivalDate">When are you in Bali? *</label>
              <input
                type="text"
                id="arrivalDate"
                name="arrivalDate"
                value={formData.arrivalDate}
                onChange={handleChange}
                placeholder="e.g. June 10 – July 5, 2025"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="contact">WhatsApp / Telegram (optional)</label>
              <input
                type="text"
                id="contact"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                placeholder="+1 234 567 890"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Anything else we should know?</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                placeholder="Any questions or additional context about your situation"
              ></textarea>
            </div>

            {formStatus.error && (
              <div className="error-message">{formStatus.error}</div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={formStatus.submitting}
            >
              {formStatus.submitting ? 'Sending...' : 'Submit Inquiry'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default IndonesiaBankAccount;
